import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import T from "../styles/tokens";
import { Badge, Btn, Modal, Spinner, Ic, fmtDate, fmtDateTime, timeAgo } from "../components/UI";
import * as api from "../utils/api";
import { TENANT_ID } from "../auth/authConfig";

// Limited to portals that respect Entra role assignments for B2B external
// admins. The classic admin.microsoft.com family (M365/Exchange/SharePoint/
// Teams) is intentionally excluded — those don't grant access to external
// users regardless of role, so the buttons would just frustrate techs.
const TENANT_PORTALS = [
  { key: "entra", name: "Entra Admin", url: (tid) => `https://entra.microsoft.com/${tid}` },
  { key: "intune", name: "Intune", url: (tid) => `https://intune.microsoft.com/${tid}` },
  { key: "security", name: "Security & Compliance", url: (tid) => `https://security.microsoft.com/?tid=${tid}` },
];

function PortalDropdown({ tenantId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  if (!tenantId) return null;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <Btn
        size="sm"
        variant="secondary"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        Open ▾
      </Btn>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 200,
            background: T.white,
            border: `1px solid ${T.gray200}`,
            borderRadius: T.radiusSm,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            zIndex: 20,
          }}
        >
          {TENANT_PORTALS.map((p, i) => (
            <a
              key={p.key}
              href={p.url(tenantId)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "9px 12px",
                fontSize: 13,
                color: T.charcoal,
                fontFamily: T.font,
                textDecoration: "none",
                borderBottom: i < TENANT_PORTALS.length - 1 ? `1px solid ${T.gray100}` : "none",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = T.gray100)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span>{p.name}</span>
              <span style={{ color: T.gray400, fontSize: 11 }}>↗</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   REGENERATE INVITATION MODAL — used by both list and detail
   ═══════════════════════════════════════════════════════════════════════════ */

function RegenerateModal({ client, onClose, onSuccess }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    if (client) {
      setPhase("idle");
      setResult(null);
      setError(null);
      setCopied(false);
      setEmailSent(false);
      setEmailError(null);
    }
    // Depend on client?.id only — a same-client refetch from onSuccess would
    // otherwise reset the modal back to idle and erase the generated link.
  }, [client?.id]);

  const open = !!client;

  const handleConfirm = async () => {
    setPhase("loading");
    setError(null);
    try {
      const res = await api.regenerateInvitation(client.id);
      setResult(res);
      setPhase("done");
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      setPhase("idle");
    }
  };

  const handleCopy = () => {
    if (result?.invitationUrl) {
      navigator.clipboard.writeText(result.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.invitationId) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      await api.sendInvitationEmail(result.invitationId);
      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Regenerate Invitation" width={520}>
      <div style={{ fontFamily: T.font }}>
        {phase === "idle" && client && (
          <>
            <p style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.6, marginBottom: 8 }}>
              Generate a new onboarding link for <strong>{client.name}</strong>.
            </p>
            <p style={{ fontSize: 12, color: T.gray400, marginBottom: 20, lineHeight: 1.6 }}>
              The current invitation link will be cancelled if it's still pending. The new link reuses the same template and Pathfinder users.
            </p>
            {error && (
              <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 16 }}>{error}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
              <Btn onClick={handleConfirm}>Regenerate</Btn>
            </div>
          </>
        )}
        {phase === "loading" && (
          <div style={{ padding: "32px 0", textAlign: "center" }}>
            <Spinner size={28} />
            <div style={{ fontSize: 13, color: T.gray400, marginTop: 12 }}>Generating new invitation…</div>
          </div>
        )}
        {phase === "done" && result && client && (
          <>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#E8F8F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: T.green }}>
                <Ic.Check />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: T.indigo, margin: 0 }}>New invitation generated</h3>
              <p style={{ fontSize: 12, color: T.gray400, marginTop: 6 }}>Share this link with {client.contactName}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.gray100, borderRadius: T.radiusSm, padding: "10px 14px", marginBottom: 14 }}>
              <input readOnly value={result.invitationUrl} style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 12, fontFamily: "monospace", color: T.charcoal }} />
              <Btn size="sm" variant={copied ? "ghost" : "primary"} onClick={handleCopy} icon={copied ? <Ic.Check /> : <Ic.Copy />}>
                {copied ? "Copied!" : "Copy Link"}
              </Btn>
              <Btn size="sm" variant={emailSent ? "ghost" : "secondary"} onClick={handleSendEmail} disabled={emailSending}>
                {emailSending ? "Sending…" : emailSent ? "Sent ✓" : "Send Email"}
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 12 }}>
              Email will be sent to <strong style={{ color: T.charcoal }}>{client.contactEmail}</strong>
            </div>
            {emailError && (
              <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 12 }}>{emailError}</div>
            )}
            <div style={{ fontSize: 12, color: T.gray400, marginBottom: 16, lineHeight: 1.6 }}>
              Expires <strong style={{ color: T.charcoal }}>{new Date(result.expiresAt).toLocaleString()}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={onClose}>Done</Btn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   OFF-BOARDING LINK MODAL — generates and displays a client-facing off-boarding URL
   ═══════════════════════════════════════════════════════════════════════════ */

function OffboardingModal({ client, onClose, onSuccess }) {
  const [phase, setPhase] = useState("idle"); // idle | loading | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    if (client) {
      setPhase("idle");
      setResult(null);
      setError(null);
      setCopied(false);
      setEmailSent(false);
      setEmailError(null);
    }
    // Depend on client?.id only — a same-client refetch from onSuccess would
    // otherwise reset the modal back to idle and erase the generated link.
  }, [client?.id]);

  const open = !!client;

  const handleConfirm = async () => {
    setPhase("loading");
    setError(null);
    try {
      const res = await api.generateOffboardingLink(client.id);
      setResult(res);
      setPhase("done");
      onSuccess?.();
    } catch (err) {
      setError(err.message);
      setPhase("idle");
    }
  };

  const handleCopy = () => {
    if (result?.offboardingUrl) {
      navigator.clipboard.writeText(result.offboardingUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleSendEmail = async () => {
    if (!result?.offboardingId) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      await api.sendOffboardingEmail(result.offboardingId);
      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Generate Off-boarding Link" width={520}>
      <div style={{ fontFamily: T.font }}>
        {phase === "idle" && client && (
          <>
            <p style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.6, marginBottom: 8 }}>
              Generate a single-use off-boarding link for <strong>{client.name}</strong>.
            </p>
            <p style={{ fontSize: 12, color: T.gray400, marginBottom: 20, lineHeight: 1.6 }}>
              The client admin uses this link to confirm and execute the disconnection themselves. After they sign in with their Microsoft 365 admin account, all Pathfinder guests and the cross-tenant policy will be removed from their tenant. The link expires in 72 hours and is single-use.
            </p>
            {error && (
              <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 16 }}>{error}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
              <Btn onClick={handleConfirm}>Generate Link</Btn>
            </div>
          </>
        )}
        {phase === "loading" && (
          <div style={{ padding: "32px 0", textAlign: "center" }}>
            <Spinner size={28} />
            <div style={{ fontSize: 13, color: T.gray400, marginTop: 12 }}>Generating off-boarding link…</div>
          </div>
        )}
        {phase === "done" && result && client && (
          <>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#E8F8F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: T.green }}>
                <Ic.Check />
              </div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: T.indigo, margin: 0 }}>Off-boarding link generated</h3>
              <p style={{ fontSize: 12, color: T.gray400, marginTop: 6 }}>Send this link to {client.contactName}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: T.gray100, borderRadius: T.radiusSm, padding: "10px 14px", marginBottom: 14 }}>
              <input readOnly value={result.offboardingUrl} style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 12, fontFamily: "monospace", color: T.charcoal }} />
              <Btn size="sm" variant={copied ? "ghost" : "primary"} onClick={handleCopy} icon={copied ? <Ic.Check /> : <Ic.Copy />}>
                {copied ? "Copied!" : "Copy Link"}
              </Btn>
              <Btn size="sm" variant={emailSent ? "ghost" : "secondary"} onClick={handleSendEmail} disabled={emailSending}>
                {emailSending ? "Sending…" : emailSent ? "Sent ✓" : "Send Email"}
              </Btn>
            </div>
            <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginBottom: 12 }}>
              Email will be sent to <strong style={{ color: T.charcoal }}>{client.contactEmail}</strong>
            </div>
            {emailError && (
              <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 12 }}>{emailError}</div>
            )}
            <div style={{ fontSize: 12, color: T.gray400, marginBottom: 16, lineHeight: 1.6 }}>
              Expires <strong style={{ color: T.charcoal }}>{new Date(result.expiresAt).toLocaleString()}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn onClick={onClose}>Done</Btn>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CLIENTS LIST
   ═══════════════════════════════════════════════════════════════════════════ */

export function ClientsList() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [regenClient, setRegenClient] = useState(null);

  const refresh = useCallback(() => {
    api.listClients().then(setClients).catch(console.error);
  }, []);

  useEffect(() => {
    api.listClients().then(setClients).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? clients : clients.filter((c) => c.status === filter);

  if (loading) return <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner size={36} /></div>;

  return (
    <div style={{ animation: "pfFadeIn 0.3s ease-out" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Clients</h1>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 4 }}>{clients.length} total clients</p>
        </div>
        <Btn onClick={() => navigate("/dashboard/invite")} icon={<Ic.Plus />}>New Invitation</Btn>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        {["all", "connected", "pending", "expired", "revoked"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: "6px 14px", borderRadius: 99, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 600, fontFamily: T.font,
              background: filter === f ? T.purple : T.white,
              color: filter === f ? T.white : T.charcoal,
              transition: "all 0.15s",
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="pf-table-wrap" style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: T.font }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.gray100}` }}>
              {["Company", "Domain", "Contact", "Template", "Status", "Health", "Connected", ""].map((h, i) => (
                <th key={i} style={{ textAlign: "left", padding: "10px 14px", fontSize: 10.5, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/dashboard/clients/${c.id}`)}
                style={{ borderBottom: `1px solid ${T.gray100}`, cursor: "pointer", transition: "background 0.1s" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.gray100)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "12px 14px", fontWeight: 600, color: T.indigo }}>{c.name}</td>
                <td style={{ padding: "12px 14px", color: T.gray400, fontSize: 12 }}>{c.domain}</td>
                <td style={{ padding: "12px 14px", color: T.charcoal }}>{c.contactName}</td>
                <td style={{ padding: "12px 14px", color: T.charcoal }}>{c.templateId}</td>
                <td style={{ padding: "12px 14px" }}><Badge status={c.status} /></td>
                <td style={{ padding: "12px 14px" }}>{c.healthStatus ? <Badge status={c.healthStatus} /> : <span style={{ color: T.gray400 }}>—</span>}</td>
                <td style={{ padding: "12px 14px", color: T.gray400, fontSize: 12 }}>{fmtDate(c.connectedAt)}</td>
                <td style={{ padding: "8px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <span style={{ display: "inline-flex", gap: 6 }}>
                    {c.status === "connected" && c.tenantId && (
                      <PortalDropdown tenantId={c.tenantId} />
                    )}
                    <Btn size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setRegenClient(c); }}>Resend</Btn>
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: "center", color: T.gray400, fontStyle: "italic" }}>No clients match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <RegenerateModal
        client={regenClient}
        onClose={() => setRegenClient(null)}
        onSuccess={refresh}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CLIENT DETAIL
   ═══════════════════════════════════════════════════════════════════════════ */

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);
  const [retryError, setRetryError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getClient(id);
      setClient(data);
    } catch (err) {
      console.error("Failed to load client:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await api.revokeClient(id);
      await load();
      setRevokeOpen(false);
    } catch (err) {
      console.error("Revoke failed:", err);
    } finally {
      setRevoking(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteClient(id);
      navigate("/dashboard/clients");
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  };

  const canDelete = client && ["pending", "revoked", "expired"].includes(client.status);

  const handleRetry = async () => {
    setRetrying(true);
    setRetryResult(null);
    setRetryError(null);
    try {
      const res = await api.retryConfiguration(id);
      setRetryResult(res);
      await load();
    } catch (err) {
      setRetryError(err.message);
    } finally {
      setRetrying(false);
    }
  };

  const canRetry =
    client &&
    client.status === "connected" &&
    client.tenantId &&
    client.tenantId !== TENANT_ID;

  if (loading) return <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}><Spinner size={36} /></div>;
  if (!client) return <div style={{ padding: 40, textAlign: "center", color: T.gray400 }}>Client not found.</div>;

  const roles = client.template?.roles || [];

  return (
    <div style={{ animation: "pfFadeIn 0.3s ease-out" }}>
      {/* Back + Header */}
      <button
        onClick={() => navigate("/dashboard/clients")}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.purple, fontSize: 13, fontWeight: 600, fontFamily: T.font, marginBottom: 16 }}
      >
        <Ic.Back /> Back to Clients
      </button>

      <div className="pf-wrap-mobile" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>{client.name}</h1>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 4 }}>{client.domain} • {client.contactEmail}</p>
        </div>
        <div className="pf-wrap-mobile" style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Badge status={client.status} />
          {client.status === "connected" && client.tenantId && (
            <Btn variant="secondary" size="sm" onClick={() => navigate(`/dashboard/clients/${id}/health`)} icon={<Ic.Dashboard />}>Tenant Health</Btn>
          )}
          {client.status === "connected" && client.tenantId && (
            <Btn variant="secondary" size="sm" onClick={() => navigate(`/dashboard/clients/${id}/manage`)} icon={<Ic.Audit />}>Administration</Btn>
          )}
          <Btn variant="secondary" size="sm" onClick={() => setRegenOpen(true)} icon={<Ic.Invite />}>Regenerate Link</Btn>
          {canRetry && (
            <Btn variant="secondary" size="sm" onClick={handleRetry} disabled={retrying}>
              {retrying ? "Retrying…" : "Retry Configuration"}
            </Btn>
          )}
          {client.status === "connected" && client.tenantId !== TENANT_ID && (
            <Btn variant="secondary" size="sm" onClick={() => setOffboardOpen(true)} icon={<Ic.Revoke />}>Generate Off-boarding Link</Btn>
          )}
          {client.status === "connected" && client.tenantId !== TENANT_ID && (
            <Btn variant="danger" size="sm" onClick={() => setRevokeOpen(true)} icon={<Ic.Revoke />}>Revoke Access</Btn>
          )}
          {canDelete && (
            <Btn variant="danger" size="sm" onClick={() => { setDeleteError(null); setDeleteOpen(true); }}>Delete Client</Btn>
          )}
        </div>
      </div>

      {/* Retry result banner */}
      {retryError && (
        <div style={{ background: T.redBg, color: T.red, padding: "12px 16px", borderRadius: T.radiusSm, fontSize: 13, fontFamily: T.font, marginBottom: 16 }}>
          Retry failed: {retryError}
        </div>
      )}
      {retryResult && (
        <div style={{
          background: retryResult.errors?.length > 0 ? T.amberBg : "#E8F8F0",
          color: retryResult.errors?.length > 0 ? T.amber : T.green,
          padding: "12px 16px", borderRadius: T.radiusSm, fontSize: 13, fontFamily: T.font, marginBottom: 16,
        }}>
          {retryResult.errors?.length > 0 ? (
            <>Retry completed with {retryResult.errors.length} step error{retryResult.errors.length === 1 ? "" : "s"}: {retryResult.errors.map((e) => e.step).join(", ")}. Check the audit log for details.</>
          ) : (
            <>Retry completed successfully — {retryResult.guestInvitations?.filter((g) => g.success).length || 0} guests, {retryResult.roleAssignments?.filter((r) => r.success).length || 0} roles configured.</>
          )}
        </div>
      )}

      {/* Info Cards */}
      <div className="pf-stack-mobile" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14, fontFamily: T.font }}>Connection Details</h4>
          <div style={{ display: "grid", gap: 10, fontSize: 13, fontFamily: T.font }}>
            <div><span style={{ color: T.gray400 }}>Contact:</span> <span style={{ color: T.charcoal, fontWeight: 500 }}>{client.contactName}</span></div>
            <div><span style={{ color: T.gray400 }}>Email:</span> <span style={{ color: T.charcoal, fontWeight: 500 }}>{client.contactEmail}</span></div>
            <div><span style={{ color: T.gray400 }}>Tenant ID:</span> <span style={{ color: T.charcoal, fontWeight: 500, fontSize: 11, fontFamily: "monospace" }}>{client.tenantId || "Not yet connected"}</span></div>
            <div><span style={{ color: T.gray400 }}>Connected:</span> <span style={{ color: T.charcoal, fontWeight: 500 }}>{fmtDateTime(client.connectedAt)}</span></div>
            {client.revokedAt && <div><span style={{ color: T.gray400 }}>Revoked:</span> <span style={{ color: T.red, fontWeight: 500 }}>{fmtDateTime(client.revokedAt)}</span></div>}
          </div>
        </div>

        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20 }}>
          <h4 style={{ fontSize: 12, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14, fontFamily: T.font }}>Permission Template</h4>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 8 }}>{client.template?.name || client.templateId}</div>
          <div style={{ fontSize: 12, color: T.charcoal, fontFamily: T.font, marginBottom: 12 }}>{client.template?.description}</div>
          {roles.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {roles.map((r) => (
                <span key={r} style={{ padding: "3px 8px", borderRadius: 4, background: "#F0EAFF", color: T.purple, fontSize: 11, fontWeight: 500, fontFamily: T.font }}>{r}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Manage Tenant — admin portal quick-launch */}
      {client.status === "connected" && client.tenantId && (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, padding: 20, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <h4 style={{ fontSize: 12, fontWeight: 600, color: T.gray400, textTransform: "uppercase", letterSpacing: "0.08em", margin: 0, fontFamily: T.font }}>Manage Tenant</h4>
            <span style={{ fontSize: 11, color: T.gray400, fontFamily: "monospace" }}>{client.tenantId}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 8 }}>
            {TENANT_PORTALS.map((p) => (
              <a
                key={p.key}
                href={p.url(client.tenantId)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: T.radiusSm,
                  border: `1px solid ${T.gray200}`,
                  background: T.white,
                  fontSize: 13,
                  fontWeight: 500,
                  color: T.indigo,
                  fontFamily: T.font,
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#F8F5FF"; e.currentTarget.style.borderColor = T.purple; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = T.white; e.currentTarget.style.borderColor = T.gray200; }}
              >
                <span>{p.name}</span>
                <span style={{ color: T.gray400, fontSize: 12 }}>↗</span>
              </a>
            ))}
          </div>
          <div style={{ marginTop: 12, fontSize: 11.5, color: T.gray400, fontFamily: T.font, lineHeight: 1.5 }}>
            Each link opens in a new tab and hints Microsoft to switch context to this tenant. If a portal lands on your home tenant, use the directory switcher (top right of the portal) to flip to <strong style={{ color: T.charcoal }}>{client.tenantDisplayName || client.name}</strong>.
          </div>
        </div>
      )}

      {/* Pathfinder Access */}
      {client.pathfinderAccess && client.pathfinderAccess.length > 0 && (
        <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}`, marginBottom: 24 }}>
          <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Pathfinder Access</h3>
            <span style={{ fontSize: 11, color: T.gray400, fontFamily: T.font }}>{client.pathfinderAccess.length} {client.pathfinderAccess.length === 1 ? "user" : "users"}</span>
          </div>
          <div>
            {client.pathfinderAccess.map((u, i) => {
              const successRoles = (u.roles || []).filter((r) => r.success);
              const failedRoles = (u.roles || []).filter((r) => !r.success);
              return (
                <div key={u.email} style={{ padding: "14px 20px", borderBottom: i < client.pathfinderAccess.length - 1 ? `1px solid ${T.gray100}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: successRoles.length > 0 || failedRoles.length > 0 ? 8 : 0 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.indigo, fontFamily: T.font }}>{u.email}</div>
                      {!u.inviteSuccess && u.inviteError && (
                        <div style={{ fontSize: 11.5, color: T.red, fontFamily: T.font, marginTop: 2 }}>Invite failed: {u.inviteError}</div>
                      )}
                      {u.inviteSuccess && u.inviteStatus === "AlreadyExists" && (
                        <div style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, marginTop: 2 }}>Already a guest</div>
                      )}
                    </div>
                    {u.inviteSuccess && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: T.green, fontFamily: T.font, padding: "2px 8px", borderRadius: 4, background: "#E8F8F0" }}>Guest invited</span>
                    )}
                  </div>
                  {successRoles.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {successRoles.map((r) => (
                        <span key={r.role} style={{ padding: "3px 8px", borderRadius: 4, background: "#F0EAFF", color: T.purple, fontSize: 11, fontWeight: 500, fontFamily: T.font }}>
                          {r.role}{r.status === "AlreadyAssigned" ? " ✓" : ""}
                        </span>
                      ))}
                    </div>
                  )}
                  {failedRoles.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {failedRoles.map((r) => (
                        <span key={r.role} title={r.error || ""} style={{ padding: "3px 8px", borderRadius: 4, background: T.redBg, color: T.red, fontSize: 11, fontWeight: 500, fontFamily: T.font }}>
                          {r.role} (failed)
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div style={{ background: T.white, borderRadius: T.radius, border: `1px solid ${T.gray200}` }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.gray200}` }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: 0 }}>Activity Log</h3>
        </div>
        <div>
          {(client.auditLog || []).map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 20px", borderBottom: `1px solid ${T.gray100}` }}>
              <Badge status={e.severity} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: T.charcoal, fontFamily: T.font }}>{e.detail}</div>
                <div style={{ fontSize: 11, color: T.gray400, marginTop: 3 }}>{e.actor} • {timeAgo(e.timestamp)}</div>
              </div>
            </div>
          ))}
          {(!client.auditLog || client.auditLog.length === 0) && (
            <div style={{ padding: 24, textAlign: "center", color: T.gray400, fontStyle: "italic" }}>No activity for this client.</div>
          )}
        </div>
      </div>

      <RegenerateModal
        client={regenOpen ? client : null}
        onClose={() => setRegenOpen(false)}
        onSuccess={load}
      />

      <OffboardingModal
        client={offboardOpen ? client : null}
        onClose={() => setOffboardOpen(false)}
        onSuccess={load}
      />

      {/* Delete Modal */}
      <Modal open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} title="Delete Client" width={480}>
        <div style={{ fontFamily: T.font }}>
          <div style={{ background: T.redBg, borderRadius: T.radiusSm, padding: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: T.red, fontWeight: 600, margin: 0 }}>This permanently deletes {client.name} and cannot be undone.</p>
          </div>
          <p style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.6, marginBottom: 6 }}>The following will be removed from the database:</p>
          <ul style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.8, paddingLeft: 20, marginBottom: 16 }}>
            <li>The client record</li>
            <li>All invitation records associated with this client's domain</li>
          </ul>
          <p style={{ fontSize: 12, color: T.gray400, marginBottom: 20, lineHeight: 1.6 }}>
            Audit log entries are preserved for compliance.
          </p>
          {deleteError && (
            <div style={{ background: T.redBg, color: T.red, padding: 10, borderRadius: T.radiusSm, fontSize: 12, marginBottom: 16 }}>{deleteError}</div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Btn>
            <Btn variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Confirm Delete"}
            </Btn>
          </div>
        </div>
      </Modal>

      {/* Revoke Modal */}
      <Modal open={revokeOpen} onClose={() => setRevokeOpen(false)} title="Revoke Access" width={480}>
        <div style={{ fontFamily: T.font }}>
          <div style={{ background: T.redBg, borderRadius: T.radiusSm, padding: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: T.red, fontWeight: 600, margin: 0 }}>This will permanently remove Pathfinder's access to {client.name}'s tenant.</p>
          </div>
          <p style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.6, marginBottom: 6 }}>The following actions will be performed:</p>
          <ul style={{ fontSize: 13, color: T.charcoal, lineHeight: 1.8, paddingLeft: 20, marginBottom: 20 }}>
            <li>Remove all Pathfinder guest accounts from the tenant</li>
            <li>Delete the cross-tenant access policy</li>
            <li>Revoke all directory role assignments</li>
          </ul>
          <p style={{ fontSize: 12, color: T.gray400, marginBottom: 20 }}>This cannot be undone. The client would need to re-consent to restore access.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="secondary" onClick={() => setRevokeOpen(false)}>Cancel</Btn>
            <Btn variant="danger" onClick={handleRevoke} disabled={revoking}>
              {revoking ? "Revoking..." : "Confirm Revoke"}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
